import { Icon } from "@chakra-ui/react";
import { EmrActions } from "@medstonetech/slate-icons";
import {
  ChartRow,
  ChartRowElement,
  ChartRowGroup,
  ChartSection,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
} from "modules";
import {
  ACTIONS_COLUMNS_GAP,
  ACTIONS_ROW_TEMPLATE_COLUMNS_3,
  ACTIONS_ROW_TEMPLATE_COLUMNS_4,
  CONTAINER_PADDING_2,
} from "modules/triage/shared/actions/constants";
import * as React from "react";
import { FormProvider } from "react-hook-form";
import { Loading, StaffAvatar } from "shared";
import { useTriageChartActionsForm } from "../../hooks";
import { ActionsHeaderDescription } from "../../shared";

const sectionId = "SACT";
const chartCode = "T035x";

function N35Actions() {
  const {
    encounterId,
    getIsLoading,
    onSubmit,
    updateIsLoading,
    useFormReturn,
    onSaveAndNext,
  } = useTriageChartActionsForm({
    chartCode: chartCode,
  });

  const { getValues } = useFormReturn;
  const initialActionsSections = getValues("initialActionsSections");
  const actionsSections = getValues("actionsSections");

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormReturn}>
      <ChartSection
        onSubmit={onSubmit}
        onRouteChangeSave={(onComplete) => {
          onSubmit();
          onComplete();
        }}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrActions} />}
          showNotesBtn={false}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
        >
          Actions
        </ChartSectionHeader>
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q001"
              subsectionLabel="Initial Actions"
            >
              Initial Actions
            </ChartSubsectionHeader>
          }
          content={<ActionsHeaderDescription />}
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[0] &&
                        initialActionsSections[0].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[0] &&
                        initialActionsSections[0].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[0].${chartCode}${sectionId}Q001A000`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ID"
                    name={`initialActionsSections[0].${chartCode}${sectionId}Q001A001`}
                    type="option"
                  />
                  <ChartRowElement
                    label="*"
                    type="label"
                    pl="unset"
                    ml="8px"
                    marginInline="unset"
                    color="red"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                  <ChartRowElement
                    pl="20px"
                    label="allergy band"
                    name={`initialActionsSections[0].${chartCode}${sectionId}Q001A002`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="applied"
                    name={`initialActionsSections[0].${chartCode}${sectionId}Q001A003`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    label="*"
                    type="label"
                    pl="unset"
                    ml="8px"
                    marginInline="unset"
                    color="red"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                  <ChartRowElement
                    label="verified"
                    name={`initialActionsSections[0].${chartCode}${sectionId}Q001A004`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    label="*"
                    type="label"
                    pl="unset"
                    ml="8px"
                    marginInline="unset"
                    color="red"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[1] &&
                        initialActionsSections[1].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[1] &&
                        initialActionsSections[1].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[1].${chartCode}${sectionId}Q001A006`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="disrobed"
                    name={`initialActionsSections[1].${chartCode}${sectionId}Q001A007`}
                    type="option"
                  />
                  <ChartRowElement
                    label="gowned"
                    name={`initialActionsSections[1].${chartCode}${sectionId}Q001A008`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="blanket provided"
                    name={`initialActionsSections[1].${chartCode}${sectionId}Q001A009`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[2] &&
                        initialActionsSections[2].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[2] &&
                        initialActionsSections[2].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[2].${chartCode}${sectionId}Q001A010`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="bandage"
                    name={`initialActionsSections[2].${chartCode}${sectionId}Q001A011`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="dressing"
                    name={`initialActionsSections[2].${chartCode}${sectionId}Q001A012`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[3] &&
                        initialActionsSections[3].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[3] &&
                        initialActionsSections[3].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[3].${chartCode}${sectionId}Q001A013`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="bed low position"
                    name={`initialActionsSections[3].${chartCode}${sectionId}Q001A014`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="side rails up"
                    name={`initialActionsSections[3].${chartCode}${sectionId}Q001A015`}
                    type="option"
                  />
                  <ChartRowElement
                    label="x 1"
                    name={`initialActionsSections[3].${chartCode}${sectionId}Q001A016`}
                    type="option"
                  />
                  <ChartRowElement
                    label="x 2"
                    name={`initialActionsSections[3].${chartCode}${sectionId}Q001A017`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[4] &&
                        initialActionsSections[4].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[4] &&
                        initialActionsSections[4].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[4].${chartCode}${sectionId}Q001A018`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="call light in reach"
                    name={`initialActionsSections[4].${chartCode}${sectionId}Q001A019`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="head of bed elevated"
                    name={`initialActionsSections[4].${chartCode}${sectionId}Q001A020`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_3}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (initialActionsSections[5] &&
                        initialActionsSections[5].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (initialActionsSections[5] &&
                        initialActionsSections[5].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`initialActionsSections[5].${chartCode}${sectionId}Q001A021`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="narcotic speech given"
                    name={`initialActionsSections[5].${chartCode}${sectionId}Q001A022`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="medication speech given"
                    name={`initialActionsSections[5].${chartCode}${sectionId}Q001A023`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q007"
              subsectionLabel="Actions"
            >
              Actions
            </ChartSubsectionHeader>
          }
          content={<ActionsHeaderDescription />}
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[0] &&
                        actionsSections[0].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[0] &&
                        actionsSections[0].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[0].${chartCode}${sectionId}Q002A001`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="cardiac monitor"
                    name={`actionsSections[0].${chartCode}${sectionId}Q002A002`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[0].${chartCode}${sectionId}Q002A003`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[1] &&
                        actionsSections[1].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[1] &&
                        actionsSections[1].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A004`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pulse oximeter"
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A005`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A006`}
                    w="120px"
                    minW="120px"
                    type="input"
                  />
                  <ChartRowElement
                    label="%"
                    type="option"
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A027`}
                  />
                  <ChartRowElement
                    label="RA /"
                    type="option"
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A028`}
                  />
                  <ChartRowElement
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A007`}
                    w="120px"
                    minW="120px"
                    type="input"
                  />
                  <ChartRowElement
                    label="L"
                    type="option"
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A029`}
                  />
                  <ChartRowElement
                    name={`actionsSections[1].${chartCode}${sectionId}Q002A008`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[2] &&
                        actionsSections[2].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[2] &&
                        actionsSections[2].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A009`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="O2"
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A010`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A011`}
                    w="120px"
                    minW="120px"
                    type="input"
                  />
                  <ChartRowElement
                    label="L"
                    type="option"
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A027`}
                  />
                  <ChartRowElement
                    label="via:"
                    type="option"
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A028`}
                  />
                  <ChartRowElement
                    label="NC"
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A012`}
                    type="option"
                  />
                  <ChartRowElement
                    label="mask"
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A013`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[2].${chartCode}${sectionId}Q002A014`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[3] &&
                        actionsSections[3].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[3] &&
                        actionsSections[3].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[3].${chartCode}${sectionId}Q002A015`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="bedside glucose"
                    name={`actionsSections[3].${chartCode}${sectionId}Q002A016`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[3].${chartCode}${sectionId}Q002A017`}
                    w="120px"
                    minW="120px"
                    type="input"
                  />
                  <ChartRowElement
                    label="mg / dL"
                    type="option"
                    name={`actionsSections[3].${chartCode}${sectionId}Q002A027`}
                  />
                  <ChartRowElement
                    name={`actionsSections[3].${chartCode}${sectionId}Q002A018`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[4] &&
                        actionsSections[4].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[4] &&
                        actionsSections[4].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[4].${chartCode}${sectionId}Q002A019`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ready for provider eval"
                    name={`actionsSections[4].${chartCode}${sectionId}Q002A020`}
                    type="option"
                  />
                  <ChartRowElement
                    label="notified provider"
                    name={`actionsSections[4].${chartCode}${sectionId}Q002A021`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[4].${chartCode}${sectionId}Q002A022`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    w="32px"
                    h="32px"
                    fontSize=".25rem"
                    userName={
                      (actionsSections[5] &&
                        actionsSections[5].createdByFullName) ||
                      ""
                    }
                    profileUrl={
                      (actionsSections[5] &&
                        actionsSections[5].createdByPictureUrl) ||
                      ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`actionsSections[5].${chartCode}${sectionId}Q002A023`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="restraints"
                    name={`actionsSections[5].${chartCode}${sectionId}Q002A024`}
                    type="option"
                  />
                  <ChartRowElement
                    label="see documentation"
                    name={`actionsSections[5].${chartCode}${sectionId}Q002A025`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    name={`actionsSections[5].${chartCode}${sectionId}Q002A026`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartSection>
    </FormProvider>
  );
}

export { N35Actions };
